import React, { useCallback, useEffect, useState } from "react";
import { C } from "../../../components/cameras/CamerasStyles";
import { adminPinFromSession } from "../../../utils/securityApi";
import { useDeadbolts, useLockControls } from "../../../features/security/useDeadbolts";
import { autoLockNote, lockActivity, lockHealthLine, lockStateView } from "../../../features/security/locks";
import { phoneUi } from "../../ui/phoneUi";

// The phone Cameras screen's deadbolts (2026-10-30): each lock's bolt, battery and signal, Lock / Unlock on the phone's
// PIN sheet, and the auto-lock switch. Layout only: rules and calls are features/security/locks.js + useDeadbolts.js.

const TONE = { ok: C.live, bad: C.bad, warn: C.warn, muted: C.faint };
const NOTE = { success: C.live, danger: C.bad, warning: C.warn, info: C.textSoft };

function LockRow({ lock, data, readFailed, onChanged, onFollow, onRefused }) {
  const { busy, msg, press, toggleAuto } = useLockControls({ lock, data, ui: phoneUi, onChanged, onFollow, onRefused });
  const [open, setOpen] = useState(false);
  const view = lockStateView(lock, readFailed);
  const activity = lockActivity(data, lock.lock_id, 5);
  return (
    <div data-testid={`phone-lock-${lock.lock_id}`} style={{ display: "grid", gap: 6, paddingTop: 6 }}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
              style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: 0, padding: 0, color: C.text, fontSize: 14, textAlign: "left" }}>
        <span style={{ flex: 1 }}>{lock.name}</span>
        <span style={{ color: TONE[view.tone], fontWeight: 700, fontSize: 13 }}>{view.label}</span>
      </button>
      {view.note && <span style={{ fontSize: 12, color: TONE[view.tone] }}>{view.note}</span>}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="jqc-btn" disabled={busy} onClick={() => press("lock")}>Lock</button>
        <button type="button" className="jqc-btn" disabled={busy} onClick={() => press("unlock")}>Unlock</button>
      </div>
      {msg && <span role="status" style={{ fontSize: 13, color: NOTE[msg.kind] || C.textSoft }}>{msg.text}</span>}
      {open && (
        <div style={{ display: "grid", gap: 4, fontSize: 12, color: C.muted }}>
          <span>{lockHealthLine(lock)}</span>
          <label style={{ display: "flex", alignItems: "center", gap: 8, color: C.textSoft, fontSize: 13 }}>
            <input type="checkbox" checked={lock.auto_lock} disabled={busy} onChange={toggleAuto} />
            Lock when the shop arms
          </label>
          <span>{autoLockNote(lock)}</span>
          {activity.map((a) => (
            <div key={a.key} style={{ display: "flex", justifyContent: "space-between", gap: 10, color: a.tone === "bad" ? C.bad : C.muted }}>
              <span>{a.text}</span><span>{a.when}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PhoneLocks() {
  const [refused, setRefused] = useState(false);
  const onRefused = useCallback(() => setRefused(true), []);
  const pin = adminPinFromSession();
  const hasPin = Boolean(pin);
  useEffect(() => setRefused(false), [pin]);   // a new PIN gets a new try
  const { data, readFailed, load, follow } = useDeadbolts({ active: hasPin && !refused, onRefused });
  const locks = (data && data.locks) || [];
  if (!hasPin || refused) {
    return (
      <div data-testid="phone-locks-pin" style={{ padding: "6px 12px", borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.muted }}>
        Deadbolts: tap the lock at the top and enter your Admin PIN to see them.
      </div>
    );
  }
  if (locks.length === 0) {
    if (!readFailed) return null;
    return (
      <div data-testid="phone-locks-failed" style={{ padding: "6px 12px", borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.bad }}>
        Deadbolts: JobQ isn't answering, so they can't be shown right now.
      </div>
    );
  }
  return (
    <div data-testid="phone-locks" style={{ padding: "6px 12px 10px", borderBottom: `1px solid ${C.border}`, display: "grid", gap: 6 }}>
      <span style={{ fontSize: 12, color: C.faint, textTransform: "uppercase" }}>Deadbolts</span>
      {locks.map((l) => (
        <LockRow key={l.lock_id} lock={l} data={data} readFailed={readFailed} onChanged={load} onFollow={follow} onRefused={onRefused} />
      ))}
    </div>
  );
}
